/**
 * MCP (Model Context Protocol) Client for Anchor Browser
 * Communicates with Anchor Browser's hosted MCP service to generate Playwright code
 */

// Global variable to store the session token getter function
let getSessionToken: (() => Promise<string | null>) | null = null

// Function to set the session token getter (called from components with Clerk context)
export function setSessionTokenGetter(getter: () => Promise<string | null>) {
  getSessionToken = getter
}

export interface MCPTool {
  name: string
  description: string
  inputSchema: any
}

export interface MCPToolCall {
  name: string
  arguments: any
}

export interface MCPResponse {
  content: Array<{
    type: 'text' | 'resource'
    text?: string
    resource?: any
  }>
  isError?: boolean
}

export interface PlaywrightCodeGeneration {
  code: string
  description: string
  steps: string[]
}

export interface CDPExecutionResult {
  success: boolean
  sessionId: string
  liveViewUrl: string
  result?: any
  error?: string
  logs?: string[]
  screenshots?: string[]
  executionTime?: number
}

export class AnchorMCPClient {
  private baseUrl: string = 'https://api.smokestack.dev/api/mcp'

  constructor(_apiKey?: string) {
    // API key is handled server-side, no need to store it
  }

  /**
   * List available MCP tools
   */
  async listTools(): Promise<MCPTool[]> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Get Clerk session token if available
      if (getSessionToken) {
        try {
          const token = await getSessionToken()
          if (token) {
            headers['Authorization'] = `Bearer ${token}`
          }
        } catch (error) {
          console.log('Clerk token not available, relying on Cloudflare Access authentication')
        }
      }

      const response = await fetch(`${this.baseUrl}/tools/list`, {
        method: 'POST',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`MCP API error: ${response.status}`)
      }

      const data = await response.json()
      return data.tools || []
    } catch (error) {
      console.error('Failed to list MCP tools:', error)
      throw error
    }
  }

  /**
   * Call an MCP tool
   */
  async callTool(toolCall: MCPToolCall): Promise<MCPResponse> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Get Clerk session token if available
      if (getSessionToken) {
        try {
          const token = await getSessionToken()
          if (token) {
            headers['Authorization'] = `Bearer ${token}`
          }
        } catch (error) {
          console.log('Clerk token not available, relying on Cloudflare Access authentication')
        }
      }

      const response = await fetch(`${this.baseUrl}/tools/call`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          name: toolCall.name,
          arguments: toolCall.arguments
        })
      })

      if (!response.ok) {
        throw new Error(`MCP API error: ${response.status}`)
      }

      const data = await response.json()
      return data || { content: [], isError: true }
    } catch (error) {
      console.error('Failed to call MCP tool:', error)
      throw error
    }
  }

  /**
   * Generate Playwright code from natural language description
   * This uses the backend MCP proxy to generate code
   */
  async generatePlaywrightCode(description: string): Promise<PlaywrightCodeGeneration> {
    const clientRequestId = Math.random().toString(36).substring(7);
    const startTime = Date.now();

    console.log(`[MCPClient:${clientRequestId}] 🚀 Starting Playwright code generation`);
    console.log(`[MCPClient:${clientRequestId}] 📝 Description: "${description}"`);
    console.log(`[MCPClient:${clientRequestId}] 🌐 Base URL: ${this.baseUrl}`);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      console.log(`[MCPClient:${clientRequestId}] 🔐 Setting up authentication...`);

      // Get Clerk session token if available
      if (getSessionToken) {
        try {
          const token = await getSessionToken()
          if (token) {
            headers['Authorization'] = `Bearer ${token}`
            console.log(`[MCPClient:${clientRequestId}] ✅ Clerk token added to headers`);
          } else {
            console.log(`[MCPClient:${clientRequestId}] ⚠️  Clerk token is null`);
          }
        } catch (error) {
          console.log(`[MCPClient:${clientRequestId}] ⚠️  Clerk token not available, relying on Cloudflare Access authentication`);
        }
      } else {
        console.log(`[MCPClient:${clientRequestId}] ⚠️  getSessionToken function not available`);
      }

      console.log(`[MCPClient:${clientRequestId}] 📤 Sending request to backend...`);
      console.log(`[MCPClient:${clientRequestId}]    - URL: ${this.baseUrl}/generate-code`);
      console.log(`[MCPClient:${clientRequestId}]    - Method: POST`);
      console.log(`[MCPClient:${clientRequestId}]    - Headers: ${Object.keys(headers).join(', ')}`);
      console.log(`[MCPClient:${clientRequestId}]    - Credentials: include`);

      const requestStartTime = Date.now();
      const response = await fetch(`${this.baseUrl}/generate-code`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          description
        })
      })

      const requestDuration = Date.now() - requestStartTime;
      console.log(`[MCPClient:${clientRequestId}] 📥 Backend response received in ${requestDuration}ms`);
      console.log(`[MCPClient:${clientRequestId}]    - Status: ${response.status} ${response.statusText}`);
      console.log(`[MCPClient:${clientRequestId}]    - Headers: ${Array.from(response.headers.entries()).map(([k, v]) => `${k}=${v}`).join(', ')}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[MCPClient:${clientRequestId}] ❌ Backend request failed:`);
        console.error(`[MCPClient:${clientRequestId}]    - Status: ${response.status}`);
        console.error(`[MCPClient:${clientRequestId}]    - Error: ${errorText}`);
        throw new Error(`Code generation failed: ${response.status} - ${errorText}`)
      }

      const data = await response.json()
      const totalDuration = Date.now() - startTime;

      console.log(`[MCPClient:${clientRequestId}] ✅ Code generation successful!`);
      console.log(`[MCPClient:${clientRequestId}] 📊 Response analysis:`);
      console.log(`[MCPClient:${clientRequestId}]    - Code length: ${data.code?.length || 0} characters`);
      console.log(`[MCPClient:${clientRequestId}]    - Steps count: ${data.steps?.length || 0}`);
      console.log(`[MCPClient:${clientRequestId}]    - AI generated: ${data.ai_generated || false}`);
      console.log(`[MCPClient:${clientRequestId}]    - Generation method: ${data.generation_method || 'unknown'}`);
      console.log(`[MCPClient:${clientRequestId}]    - Confidence: ${data.confidence || 'unknown'}`);
      console.log(`[MCPClient:${clientRequestId}]    - Test name: ${data.test_name || 'unknown'}`);
      console.log(`[MCPClient:${clientRequestId}]    - Backend processing time: ${data.processing_time_ms || 'unknown'}ms`);
      console.log(`[MCPClient:${clientRequestId}]    - Total client time: ${totalDuration}ms`);

      if (data.website_analysis) {
        console.log(`[MCPClient:${clientRequestId}] 🔬 Website analysis:`);
        console.log(`[MCPClient:${clientRequestId}]    - Page type: ${data.website_analysis.page_type}`);
        console.log(`[MCPClient:${clientRequestId}]    - Forms: ${data.website_analysis.forms_count}`);
        console.log(`[MCPClient:${clientRequestId}]    - Buttons: ${data.website_analysis.buttons_count}`);
        console.log(`[MCPClient:${clientRequestId}]    - Inputs: ${data.website_analysis.inputs_count}`);
        console.log(`[MCPClient:${clientRequestId}]    - Patterns: ${data.website_analysis.common_patterns?.join(', ') || 'none'}`);
      }

      if (data.steps && data.steps.length > 0) {
        console.log(`[MCPClient:${clientRequestId}] 📋 Generated steps:`);
        data.steps.forEach((step: string, index: number) => {
          console.log(`[MCPClient:${clientRequestId}]    ${index + 1}. ${step}`);
        });
      }

      if (data.selectors && data.selectors.length > 0) {
        console.log(`[MCPClient:${clientRequestId}] 🎯 Key selectors: ${data.selectors.slice(0, 3).join(', ')}`);
      }

      if (data.actions && data.actions.length > 0) {
        console.log(`[MCPClient:${clientRequestId}] ⚡ Actions: ${data.actions.join(', ')}`);
      }

      return {
        code: data.code || '',
        description: data.description || `Generated Playwright code`,
        steps: data.steps || []
      }
    } catch (error) {
      const totalDuration = Date.now() - startTime;
      console.error(`[MCPClient:${clientRequestId}] ❌ Code generation failed after ${totalDuration}ms:`, error)
      console.log(`[MCPClient:${clientRequestId}] 🔄 Falling back to local generation...`);

      // Fallback to local generation
      const fallbackResult = this.generateCodeFromDescription(description);
      console.log(`[MCPClient:${clientRequestId}] ✅ Local fallback completed`);
      console.log(`[MCPClient:${clientRequestId}]    - Fallback code length: ${fallbackResult.code.length} characters`);

      return fallbackResult;
    }
  }

  /**
   * Execute Playwright code directly via CDP on Anchor Browser session
   */
  async executePlaywrightCodeViaCDP(
    code: string,
    sessionId?: string,
    createNewSession: boolean = false,
    timeout: number = 60000
  ): Promise<CDPExecutionResult> {
    const clientRequestId = Math.random().toString(36).substring(7);
    const startTime = Date.now();

    console.log(`[MCPClient:${clientRequestId}] 🚀 Starting CDP Playwright execution`);
    console.log(`[MCPClient:${clientRequestId}] 📝 Code length: ${code.length} characters`);
    console.log(`[MCPClient:${clientRequestId}] 🌐 Session ID: ${sessionId || 'new session'}`);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Get Clerk session token if available
      if (getSessionToken) {
        try {
          const token = await getSessionToken()
          if (token) {
            headers['Authorization'] = `Bearer ${token}`
            console.log(`[MCPClient:${clientRequestId}] ✅ Clerk token added to headers`);
          }
        } catch (error) {
          console.log(`[MCPClient:${clientRequestId}] ⚠️  Clerk token not available, relying on Cloudflare Access authentication`);
        }
      }

      console.log(`[MCPClient:${clientRequestId}] 📤 Sending CDP execution request...`);

      const requestStartTime = Date.now();
      const response = await fetch(`${this.baseUrl}/execute-playwright-code`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          code,
          sessionId,
          createNewSession,
          timeout
        })
      })

      const requestDuration = Date.now() - requestStartTime;
      console.log(`[MCPClient:${clientRequestId}] 📥 CDP response received in ${requestDuration}ms`);
      console.log(`[MCPClient:${clientRequestId}]    - Status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[MCPClient:${clientRequestId}] ❌ CDP execution failed:`);
        console.error(`[MCPClient:${clientRequestId}]    - Status: ${response.status}`);
        console.error(`[MCPClient:${clientRequestId}]    - Error: ${errorText}`);
        throw new Error(`CDP execution failed: ${response.status} - ${errorText}`)
      }

      const data = await response.json()
      const totalDuration = Date.now() - startTime;

      console.log(`[MCPClient:${clientRequestId}] ${data.success ? '✅' : '❌'} CDP execution completed!`);
      console.log(`[MCPClient:${clientRequestId}] 📊 Execution analysis:`);
      console.log(`[MCPClient:${clientRequestId}]    - Success: ${data.success}`);
      console.log(`[MCPClient:${clientRequestId}]    - Session ID: ${data.sessionId}`);
      console.log(`[MCPClient:${clientRequestId}]    - Execution time: ${data.executionTime || 'unknown'}ms`);
      console.log(`[MCPClient:${clientRequestId}]    - Logs count: ${data.logs?.length || 0}`);
      console.log(`[MCPClient:${clientRequestId}]    - Screenshots: ${data.screenshots?.length || 0}`);
      console.log(`[MCPClient:${clientRequestId}]    - Total client time: ${totalDuration}ms`);

      if (data.error) {
        console.error(`[MCPClient:${clientRequestId}] ❌ Execution error: ${data.error}`);
      }

      if (data.logs && data.logs.length > 0) {
        console.log(`[MCPClient:${clientRequestId}] 📋 Execution logs:`);
        data.logs.forEach((log: string, index: number) => {
          console.log(`[MCPClient:${clientRequestId}]    ${index + 1}. ${log}`);
        });
      }

      return {
        success: data.success,
        sessionId: data.sessionId,
        liveViewUrl: data.liveViewUrl,
        result: data.result,
        error: data.error,
        logs: data.logs || [],
        screenshots: data.screenshots || [],
        executionTime: data.executionTime || totalDuration
      }

    } catch (error) {
      const totalDuration = Date.now() - startTime;
      console.error(`[MCPClient:${clientRequestId}] ❌ CDP execution failed after ${totalDuration}ms:`, error)

      return {
        success: false,
        sessionId: sessionId || '',
        liveViewUrl: '',
        error: error instanceof Error ? error.message : 'Unknown CDP execution error',
        logs: [],
        screenshots: [],
        executionTime: totalDuration
      }
    }
  }

  /**
   * Extract URL from task description
   */
  private extractUrlFromDescription(description: string): string {
    const urlRegex = /(https?:\/\/[^\s]+)/gi
    const matches = description.match(urlRegex)
    return matches?.[0] || 'https://example.com'
  }



  /**
   * Generate Playwright code from description (fallback)
   */
  private generateCodeFromDescription(description: string): PlaywrightCodeGeneration {
    const url = this.extractUrlFromDescription(description)
    const steps: string[] = []
    let code = `import { test, expect } from '@playwright/test';

test('${description}', async ({ page }) => {
  // Navigate to the page
  await page.goto('${url}');
  
`

    // Analyze description for common patterns
    if (description.toLowerCase().includes('search')) {
      steps.push('Navigate to page', 'Find search input', 'Enter search term', 'Submit search')
      code += `  // Find and fill search input
  const searchInput = page.locator('input[type="search"], input[name*="search"], input[placeholder*="search"]').first();
  await searchInput.fill('your search term');
  
  // Submit search
  await searchInput.press('Enter');
  
`
    }

    if (description.toLowerCase().includes('click')) {
      steps.push('Find element to click', 'Click element')
      code += `  // Click element
  await page.click('selector-here');
  
`
    }

    if (description.toLowerCase().includes('form') || description.toLowerCase().includes('fill')) {
      steps.push('Find form fields', 'Fill form data', 'Submit form')
      code += `  // Fill form fields
  await page.fill('input[name="field1"]', 'value1');
  await page.fill('input[name="field2"]', 'value2');
  
  // Submit form
  await page.click('button[type="submit"]');
  
`
    }

    code += `  // Wait for navigation or response
  await page.waitForLoadState('networkidle');
  
  // Add assertions
  await expect(page).toHaveTitle(/expected title/);
});`

    return {
      code,
      description: `Generated Playwright code for: ${description}`,
      steps: steps.length > 0 ? steps : ['Navigate to page', 'Perform action', 'Verify result']
    }
  }



  /**
   * Test MCP connection
   */
  async testConnection(): Promise<boolean> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Get Clerk session token if available
      if (getSessionToken) {
        try {
          const token = await getSessionToken()
          if (token) {
            headers['Authorization'] = `Bearer ${token}`
          }
        } catch (error) {
          console.log('Clerk token not available, relying on Cloudflare Access authentication')
        }
      }

      const response = await fetch(`${this.baseUrl}/test-connection`, {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        return false
      }

      await response.json()
      // Consider it connected if we get a response, even if MCP service itself is unavailable
      // The fallback code generation will still work
      return true
    } catch (error) {
      console.error('MCP connection test failed:', error)
      return false
    }
  }
}

/**
 * Create MCP client instance
 */
export function createMCPClient(_apiKey?: string): AnchorMCPClient {
  return new AnchorMCPClient()
}
